import type { Editor } from '@/modules/Editor/editor'
import { TilesManager, Events as TilesManagerEvents } from '@/modules/Editor/managers/tilesManager'
import { Object2D } from '@/modules/Editor/models/layers/object2D'
import type { Tile } from '@/modules/Editor/models/tiler'
import { renderTiledImage } from '@/modules/Editor/renderTiledImage'
import { View } from '@/modules/Editor/views/view'
import type { FramesLoaderConfig } from '@/modules/Editor/workers/FramesLoaderWorker/types'
// eslint-disable-next-line boundaries/element-types
import type { V2DatasetItemPayload } from '@/store/types/V2DatasetItemPayload'
// eslint-disable-next-line boundaries/element-types
import type { V2DatasetItemSlot } from '@/store/types/V2DatasetItemSlot'

import { ViewTypes } from './viewTypes'
// eslint-disable-next-line boundaries/element-types
import type { RenderableImage } from '@/store/types'
import { ViewEvents } from '@/modules/Editor/eventBus'
import { getWindowLevelsForRendering } from '@/modules/Editor/utils/windowLevels'

export class TiledView extends View {
  readonly type = ViewTypes.TILED

  public tilesManager: TilesManager
  private visibleTiles: Tile[] = []

  constructor(
    public editor: Editor,
    file: V2DatasetItemSlot,
    item: V2DatasetItemPayload,
    initialFrameIndex: number = -1,
    framesLoaderConfig: FramesLoaderConfig = {},
  ) {
    super(editor, file, item, initialFrameIndex, framesLoaderConfig)

    const neighbourTiles = editor.neighbourTiles

    this.tilesManager = new TilesManager(this)

    this.visibleTiles = this.tilesManager.getVisibleTiles(neighbourTiles, false)

    this.mainLayer.onBeforeRender(
      () => (this.visibleTiles = this.tilesManager.getVisibleTiles(neighbourTiles)),
    )

    this.init()

    const handleTilesLoaded = (): void => {
      this.loading = false
    }

    this.tilesManager.on(TilesManagerEvents.TILES_LOADED, handleTilesLoaded)
    this.onCleanup.push(() =>
      this.tilesManager.off(TilesManagerEvents.TILES_LOADED, handleTilesLoaded),
    )

    const onImageFilterChanged = (): void => {
      this.tilesManager.reloadVisibleTiles()
    }
    ViewEvents.imageFilterChanged.on(onImageFilterChanged)
    this.onCleanup.push(() => ViewEvents.imageFilterChanged.off(onImageFilterChanged))
  }

  /** Override the view method to reload the tiles **/
  reloadCurrentFrame(): Promise<void> {
    // remove cached content for current tiles, as it's likely stale when reload is needed
    this.tilesManager.cleanup()
    this.mainLayer.changed()
    return Promise.resolve()
  }

  async init(): Promise<void> {
    this.camera.setImage({
      width: this.tilesManager.imageWidth,
      height: this.tilesManager.imageHeight,
    })
    this.scaleToFit()

    this.showFramesTool = false

    this.mainLayer.clear()
    this.mainLayer.add(
      new Object2D('tiled_image', () => {
        if (!this.mainLayer.context) {
          return
        }
        renderTiledImage(this, this.visibleTiles, this.mainLayer.context)
      }),
    )

    // Reset the current image filter's window level
    this.setImageFilter({
      ...this.imageFilter,
      windowLevels: getWindowLevelsForRendering(this),
    })

    // Reset tool
    const { currentTool } = this.toolManager
    if (currentTool) {
      currentTool.tool.reset(currentTool.context)
    }

    await this.jumpToFrame(this.currentFrameIndex)
    this.initCamera()
  }

  // `loadFrame` is not relevant for tiled views, and it needs to be overridden to avoid
  // unecessary http calls
  loadFrame(): Promise<RenderableImage | undefined> {
    return Promise.resolve(this.currentFrame)
  }

  get currentViewSize(): { width: number; height: number } | null {
    return this.tilesManager
      ? { width: this.tilesManager.imageWidth, height: this.tilesManager.imageHeight }
      : null
  }
}
